/**
 * @file Spec wire codec — RegExp ↔ JSON round-trip for `spec.json`.
 * @module @labcraft/course-kit/wire
 *
 * `Detector.match` / `Detector.cwd` admit `RegExp` so inspection beats
 * can accept a *family* of spellings ("prove `nix` is absent" admits
 * `command -v nix`, `which nix`, `type nix`, with or without a
 * `|| echo …` tail) instead of stranding a learner on one verbatim
 * incantation. JSON has no RegExp — `JSON.stringify` silently turns
 * one into `{}` — so the boundary is explicit:
 *
 *   - The **builder** serializes `spec.json` / `manifest.json` with
 *     {@link specWireReplacer}: `RegExp` →
 *     `{ "$regex": { "pattern": "...", "flags": "..." } }`.
 *   - The **platform** (lab SPA, or any consumer that JSON-parses a
 *     built spec) calls {@link reviveSpecWire} on the parsed value
 *     before handing it to the runtime scanner, turning every
 *     `$regex` node back into a live `RegExp`.
 *
 * In-process consumers (course unit tests via `extractChapterSpec`,
 * the integration walk via `CourseBuilder.run()`'s returned manifest)
 * hold live `RegExp` values throughout and never touch this codec.
 *
 * Browser-safe by design: no `node:*` imports — the reviver runs in
 * the SPA.
 */

/** Sentinel property name marking an encoded RegExp on the wire. */
const REGEX_KEY = '$regex'

/** The wire shape {@link specWireReplacer} emits for one RegExp. */
type RegexWireNode = { [REGEX_KEY]: { pattern: string; flags: string } }

/**
 * Whether a parsed JSON node is an encoded RegExp.
 *
 * @param value - any parsed JSON value
 * @returns true when the node carries the `$regex` sentinel shape
 */
function isRegexWireNode(value: unknown): value is RegexWireNode {
  if (typeof value !== 'object' || value === null) return false
  const node = (value as Record<string, unknown>)[REGEX_KEY]
  if (typeof node !== 'object' || node === null) return false
  const { pattern, flags } = node as Record<string, unknown>
  return typeof pattern === 'string' && typeof flags === 'string'
}

/**
 * `JSON.stringify` replacer encoding `RegExp` values as `$regex` wire
 * nodes. Pass as the second argument wherever a spec (or a manifest
 * embedding specs) is serialized.
 *
 * @param _key - property name (unused; the encoding is value-driven)
 * @param value - the property value being serialized
 * @returns the wire node for a RegExp, the value untouched otherwise
 */
export function specWireReplacer(_key: string, value: unknown): unknown {
  if (value instanceof RegExp) {
    return { [REGEX_KEY]: { pattern: value.source, flags: value.flags } }
  }
  return value
}

/**
 * Deep-revive a JSON-parsed spec (or any value embedding one): every
 * `$regex` wire node becomes a live `RegExp`; everything else is
 * copied structurally. Call once on a fetched/parsed spec before
 * handing it to the runtime scanner.
 *
 * @param value - the parsed JSON value
 * @returns the same shape with every encoded RegExp revived
 */
export function reviveSpecWire<T>(value: T): T {
  if (isRegexWireNode(value)) {
    const { pattern, flags } = value[REGEX_KEY]
    return new RegExp(pattern, flags) as unknown as T
  }
  if (Array.isArray(value)) {
    return value.map(item => reviveSpecWire(item)) as unknown as T
  }
  if (typeof value === 'object' && value !== null) {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) {
      out[key] = reviveSpecWire(item)
    }
    return out as T
  }
  return value
}
